import { parse } from 'date-fns';
import { useMemo } from 'react';
import { getDatePickerDateType, getTimePickerDateType } from 'ui';
import type { Activity, Reminder, Task } from 'models/activity';

import type { ActivityFormValues } from '../ActivityForm';

const defaultTextTask = (): Task => ({ input: 'text', kind: 'default', name: '' });

type P = {
  activity: Activity;
  edit: boolean;
};

/**
 * @deprecated AI-generated code, not reviewed. Needs revision before further use — do not use as a reference or pattern to follow.
 */
export const useGetActivityInitialValues = ({ activity, edit }: P): Partial<ActivityFormValues> =>
  useMemo(() => {
    const timezoneTimes = activity.times.map((time) => ({
      ...time,
      time: getTimePickerDateType(parse(time.time, 'HH:mm:ssX', new Date())),
    }));

    return {
      /* eslint-disable @typescript-eslint/naming-convention -- Matches the activity form's field names */
      description: activity.description,
      end_time: getDatePickerDateType(activity.endTime),
      html_description: activity.htmlDescription,
      is_dexcom: activity.isDexcom,
      is_withings: activity.isWithings,
      name: activity.name,
      periodicity_type: activity.periodicityType,
      programs_topic_id: activity.programsTopicId,
      reminders: edit
        ? activity.reminders?.map((reminder: Reminder) => {
            if (reminder.minutes % 1440 === 0) {
              return { ...reminder, minutes: reminder.minutes / 1440, type: 1440 };
            }

            if (reminder.minutes % 60 === 0) {
              return { ...reminder, minutes: reminder.minutes / 60, type: 60 };
            }

            return { ...reminder, type: 1 };
          })
        : [
            { minutes: 7, type: 60 },
            { minutes: 14, type: 60 },
          ],
      start_time: getDatePickerDateType(activity.startTime),
      tasks: edit
        ? activity.tasks.map((task) => ({
            ...task,
            bulk: task.options?.length ? [task.options.map((option) => option.name).join(',')] : undefined,
          }))
        : [defaultTextTask()],
      times: timezoneTimes,
      /* eslint-enable @typescript-eslint/naming-convention -- Matches the activity form's field names */
    };
  }, [activity, edit]);
